import { getFlag } from '@rsos/feature-flags';
import type {
  ReverseGeocodeAddressObject,
  ReverseGeocodeResult,
} from '../reverseGeocode/helpers';

/**
 * Client for the RapidSOS geocoder: a self-hosted Nominatim (OpenStreetMap)
 * behind a bearer token. When the `rsos-geocoder` flag is on it is the first
 * step of every geocode chain: RapidSOS > local GIS > Google > Esri.
 *
 * The URL and token come from `REACT_APP_RSOS_GEOCODER_URL` and
 * `REACT_APP_RSOS_GEOCODER_TOKEN` at build time. With either unset the
 * geocoder is disabled regardless of the flag, so no build ships a token
 * unless one was set on purpose.
 */

export const RSOS_GEOCODER_FLAG = 'rsos-geocoder';

/** `_serviceUrl` marker on place candidates and suggestions from this geocoder. */
export const RSOS_PLACE_SOURCE = 'rsos-geocoder';

/** OpenStreetMap's ODbL licence requires this wherever a result is shown. */
export const RSOS_ATTRIBUTION = '© OpenStreetMap contributors';

const REQUEST_TIMEOUT_MS = 3000;

/** Half-width in degrees (~55 km) of the box that biases results toward the map center. */
const BIAS_DEGREES = 0.5;

const SEARCH_CACHE_TTL_MS = 30_000;
const MAX_REGISTERED_SUGGESTIONS = 200;

const getConfig = () => ({
  url: (process.env.REACT_APP_RSOS_GEOCODER_URL || '').replace(/\/+$/, ''),
  token: process.env.REACT_APP_RSOS_GEOCODER_TOKEN || '',
});

export const isRsosGeocoderEnabled = (): boolean => {
  const { url, token } = getConfig();
  return !!url && !!token && getFlag(RSOS_GEOCODER_FLAG, false);
};

interface NominatimAddress {
  house_number?: string;
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  hamlet?: string;
  village?: string;
  town?: string;
  city?: string;
  municipality?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country?: string;
  country_code?: string;
}

export interface NominatimPlace {
  place_id: number;
  lat: string;
  lon: string;
  name?: string;
  display_name: string;
  category?: string;
  type?: string;
  addresstype?: string;
  place_rank?: number;
  address?: NominatimAddress;
}

/** Same shape as the Esri `findAddressCandidates` candidates the search UI renders. */
export interface GeocodeCandidate {
  address: string;
  location: { x: number; y: number };
  attributes: Record<string, unknown>;
  score: number;
  _serviceUrl: string;
  _token: null;
}

export interface GeocodeSuggestion {
  text: string;
  magicKey: string;
  _serviceUrl: string;
  _token: null;
}

const NON_POI_CATEGORIES = new Set(['highway', 'place', 'boundary']);

const cityOf = (address: NominatimAddress = {}): string =>
  address.city ||
  address.town ||
  address.village ||
  address.hamlet ||
  address.municipality ||
  address.suburb ||
  '';

const streetLineOf = (address: NominatimAddress = {}): string =>
  [address.house_number, address.road].filter(Boolean).join(' ');

const isPoi = (place: NominatimPlace): boolean =>
  !!place.name &&
  place.name !== place.address?.road &&
  !NON_POI_CATEGORIES.has(place.category || '');

const addrTypeOf = (place: NominatimPlace): string => {
  if (isPoi(place)) return 'POI';
  if (place.address?.house_number) return 'PointAddress';
  if (place.address?.road) return 'StreetName';
  return 'Locality';
};

const SCORE_BY_ADDR_TYPE: Record<string, number> = {
  POI: 100,
  PointAddress: 100,
  StreetName: 90,
  Locality: 80,
};

/** The part after the street line, e.g. "Charleston, West Virginia 25305". */
const placeAddressOf = (address: NominatimAddress = {}): string =>
  [cityOf(address), [address.state, address.postcode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

/**
 * A compact single-line address in the Esri `Match_addr` style, rather than
 * Nominatim's long `display_name`. A point of interest leads with its name, as
 * Esri's `LongLabel` does.
 */
export const formatRsosAddress = (place: NominatimPlace): string => {
  const street = streetLineOf(place.address);
  const head = isPoi(place)
    ? [place.name, street].filter(Boolean).join(', ')
    : street || place.name || '';
  return (
    [head, placeAddressOf(place.address)].filter(Boolean).join(', ') ||
    place.display_name
  );
};

export const toRsosAddressObject = (
  place: NominatimPlace,
): ReverseGeocodeAddressObject => ({
  home: place.address?.house_number || '',
  street: place.address?.road || '',
  city: cityOf(place.address),
  region: place.address?.state || '',
  postal_code: place.address?.postcode || '',
  country: (place.address?.country_code || '').toUpperCase(),
});

export const toRsosCandidate = (place: NominatimPlace): GeocodeCandidate => {
  const addrType = addrTypeOf(place);
  const poi = addrType === 'POI';
  const street = streetLineOf(place.address);
  return {
    address: formatRsosAddress(place),
    location: { x: Number(place.lon), y: Number(place.lat) },
    attributes: {
      Addr_type: addrType,
      ShortLabel: poi ? place.name : street || place.name,
      Place_addr: poi
        ? [street, placeAddressOf(place.address)].filter(Boolean).join(', ')
        : placeAddressOf(place.address),
      PlaceName: poi ? place.name : undefined,
      Type: poi ? place.type : undefined,
      City: cityOf(place.address),
      Region: place.address?.state,
      Postal: place.address?.postcode,
      Attribution: RSOS_ATTRIBUTION,
    },
    score: SCORE_BY_ADDR_TYPE[addrType],
    _serviceUrl: RSOS_PLACE_SOURCE,
    _token: null,
  };
};

const request = async <T>(
  path: string,
  params: Record<string, string>,
): Promise<T> => {
  const { url, token } = getConfig();
  const query = new URLSearchParams({
    ...params,
    format: 'jsonv2',
    addressdetails: '1',
  });
  const response = await fetch(`${url}${path}?${query}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `RapidSOS geocoder ${path} failed: HTTP ${response.status}`,
    );
  }
  return response.json() as Promise<T>;
};

export interface RsosForwardGeocodeArgs {
  text: string;
  latitude?: number;
  longitude?: number;
  limit?: number;
}

// The map search asks for suggestions and candidates for the same text at the
// same moment, so identical lookups share one request.
const searchCache = new Map<
  string,
  { expires: number; promise: Promise<GeocodeCandidate[]> }
>();

/**
 * Forward geocode free text. Resolves to [] when nothing matched and rejects
 * when the geocoder failed, so callers can fall through to the next step.
 */
export const rsosForwardGeocode = ({
  text,
  latitude,
  longitude,
  limit = 5,
}: RsosForwardGeocodeArgs): Promise<GeocodeCandidate[]> => {
  const params: Record<string, string> = {
    q: text.trim(),
    limit: String(limit),
    countrycodes: 'us',
  };
  if (latitude != null && longitude != null) {
    params.viewbox = [
      longitude - BIAS_DEGREES,
      latitude + BIAS_DEGREES,
      longitude + BIAS_DEGREES,
      latitude - BIAS_DEGREES,
    ].join(',');
  }

  const key = JSON.stringify(params);
  const cached = searchCache.get(key);
  if (cached && cached.expires > Date.now()) return cached.promise;

  const promise = request<NominatimPlace[]>('/search', params).then(places =>
    Array.isArray(places) ? places.map(toRsosCandidate) : [],
  );
  promise.catch(() => searchCache.delete(key));
  searchCache.set(key, { expires: Date.now() + SEARCH_CACHE_TTL_MS, promise });
  return promise;
};

/** Reverse geocode a point. Resolves to null when there is no address there. */
export const rsosReverseGeocode = async ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): Promise<ReverseGeocodeResult | null> => {
  const place = await request<NominatimPlace & { error?: string }>('/reverse', {
    lat: String(latitude),
    lon: String(longitude),
    zoom: '18',
  });
  if (!place || place.error) return null;
  return {
    address: formatRsosAddress(place),
    addressObject: toRsosAddressObject(place),
    rgAddressProvider: 'RapidSOS Geocoder',
  };
};

// Nominatim has no suggest/magicKey flow. A suggestion carries a synthetic
// magicKey that resolves back to the candidate it was built from.
const suggestedCandidates = new Map<string, GeocodeCandidate>();

export const toRsosSuggestions = (
  candidates: GeocodeCandidate[],
): GeocodeSuggestion[] =>
  candidates.map(candidate => {
    const magicKey = `rsos:${candidate.location.y},${candidate.location.x}:${candidate.address}`;
    suggestedCandidates.set(magicKey, candidate);
    if (suggestedCandidates.size > MAX_REGISTERED_SUGGESTIONS) {
      const oldest = suggestedCandidates.keys().next().value;
      if (oldest !== undefined) suggestedCandidates.delete(oldest);
    }
    return {
      text: candidate.address,
      magicKey,
      _serviceUrl: RSOS_PLACE_SOURCE,
      _token: null,
    };
  });

export const getRsosSuggestedCandidate = (
  magicKey: string,
): GeocodeCandidate | undefined => suggestedCandidates.get(magicKey);
